import classNames from "classnames";
import { compile } from "path-to-regexp";
import { Link, useLocation, useParams, useRouteMatch, LinkProps } from "react-router-dom";
import { Item, Rune } from "@outplayed/tooltips";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { useChampionProfileContext } from "@ugg/shared/components/Champions/ChampionProfileContext";
import { multibuildTransformMap, capitalizeMulti, convertObjectToArray, TabData } from "@ugg/shared/utils/multi-build-helpers";
import { KaynFormIds, Multibuilds } from "@ugg/shared/interfaces/champions/overview.interface";

interface BuildTabsProps {
  className?: string;
  version: string;
  tabData: TabData;
  onClick?: (multi: string, championName: string, index: number) => void;
}

export default function BuildTabsContainer(props: BuildTabsProps) {
  const { className, version, tabData, onClick } = props;
  const location = useLocation();
  const { championId } = useChampionProfileContext();
  const { getNormalizedChampionName } = getRiotAssetsContext();
  const normalizedChampionName = getNormalizedChampionName(championId);
  const urlParams = useParams<{ role: string; multi: string; gameMode: string }>();
  const { path } = useRouteMatch();

  const isKayn = championId == KaynFormIds.DEFAULT;
  const tabDataArray = convertObjectToArray(tabData);

  const multiURL = (multi: string) => {
    const toPath = compile(path);
    let currentMultiParam = multi !== Multibuilds.RECOMMENDED ? multi : undefined;

    return {
      pathname: toPath({ ...urlParams, multi: currentMultiParam }),
      search: location.search,
    };
  };

  return (
    <div className={classNames("flex gap-[12px]", className)}>
      {isKayn ? (
        [Multibuilds.REDKAYN, Multibuilds.BLUEKAYN].map((val, idx) => {
          const data = tabData?.[String(multibuildTransformMap[val])];

          return (
            <BuildTab
              version={version}
              label={`${capitalizeMulti(val)} Kayn`}
              runeId={data?.keystone ?? 0}
              itemId={data?.item ?? 0}
              isActive={urlParams?.multi === val || (val === Multibuilds.REDKAYN && !urlParams?.multi)}
              key={idx}
              onClick={() => onClick?.(val, normalizedChampionName, idx)}
              linkProps={{ to: multiURL(val) }}
            />
          );
        })
      ) : (
        <>
          {tabDataArray.map((data, idx) => {
            const multiData = data[0];
            return (
              <BuildTab
                version={version}
                label={capitalizeMulti(multiData)}
                runeId={data[1].keystone || 0}
                itemId={data[1].item || 0}
                isActive={urlParams?.multi === multiData || (multiData === Multibuilds.RECOMMENDED && !urlParams?.multi)}
                isDefault={multiData === Multibuilds.RECOMMENDED}
                key={idx}
                onClick={() => onClick?.(multiData, normalizedChampionName, idx)}
                linkProps={{ to: multiURL(multiData) }}
              />
            );
          })}
        </>
      )}
    </div>
  );
}

interface BuildTabProps {
  version: string;
  label?: string;
  runeId: number;
  itemId: number;
  isActive: boolean;
  isDefault?: boolean;
  linkProps: LinkProps;
  onClick?: () => void;
}

function BuildTab(props: BuildTabProps) {
  const { version, label, isActive, isDefault, runeId, itemId, linkProps, onClick } = props;

  return (
    <Link
      className={classNames("flex items-center px-[12px] py-[10px] h-[42px] rounded-t-[3px] hover:cursor-pointer", {
        "bg-purple-200": !isActive,
        "bg-[#1f1f41]": isActive,
        "shadow-[inset_0px_2px_0px_rgba(50,115,250,0.5),inset_0px_-2px_2px_rgba(0,0,0,0.2)]": !isDefault && !isActive,
        "shadow-[inset_0px_2px_0px_rgba(255,155,0,0.5),inset_0px_-2px_4px_rgba(0,0,0,0.2)]": isDefault && !isActive,
        "shadow-[inset_0px_2px_0px_#3273fa]": !isDefault && isActive,
        "shadow-[inset_0px_2px_0px_#ff9b00]": isDefault && isActive,
      })}
      {...linkProps}
      onClick={() => onClick?.()}
    >
      {label && (
        <div className="relative font-['Inter'] text-[14px] leading-[20px] tracking-[0px] text-left whitespace-nowrap">
          <span className={classNames("text-lavender-300 font-medium", { "opacity-0": isActive })}>{label}</span>
          <strong className={classNames("absolute left-0 text-white font-bold", { "opacity-0": !isActive })}>{label}</strong>
        </div>
      )}
      <div
        className={classNames(
          "flex items-center justify-center ml-[12px] w-[20px] h-[20px] rounded-[1px] bg-purple-400 opacity-50",
          { "!opacity-100": isActive },
        )}
      >
        {runeId > 0 && <Rune className="w-full" patch={version} runeId={runeId} ssr />}
      </div>
      <div
        className={classNames(
          "flex items-center justify-center ml-[6px] w-[20px] h-[20px] rounded-[1px] bg-purple-400 opacity-50",
          { "!opacity-100": isActive },
        )}
      >
        {itemId > 0 && <Item className="w-full h-full rounded-[1px]" itemId={itemId} ssr />}
      </div>
    </Link>
  );
}
